import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';
import { FormRow, overlayStyle, modalStyle } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SCOPE_TYPES = ['company', 'department', 'team', 'employee', 'external_user', 'lead_source', 'client', 'product_category', 'product', 'service_category', 'service', 'sales_type', 'combination'];
const CALC_TYPES = ['percentage', 'fixed', 'tiered_percentage', 'tiered_fixed', 'recurring_percentage', 'recurring_fixed', 'declining_recurring', 'changing_recurring', 'custom_combination'];
const CALC_BASIS = ['gross_sale', 'net_sale', 'after_discount', 'amount_collected', 'profit', 'margin', 'product_value', 'service_value', 'custom'];
const EARNING_TRIGGERS = ['deal_won', 'contract_confirmed', 'invoice_issued', 'customer_payment', 'full_settlement', 'custom'];
const CAP_TYPES = ['no_cap', 'per_transaction', 'per_month', 'per_quarter', 'per_year', 'per_agreement', 'per_beneficiary'];
const STATUSES = ['draft', 'active', 'inactive', 'archived'];

export default function CommissionProfiles() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // profile object or 'new'

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/profiles?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const changeStatus = async (id, newStatus) => {
    try {
      await fetch(`${API}/companies/${companyId}/commissions/profiles/${id}/status`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchList();
    } catch (e) { console.error(e); }
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Delete this draft profile?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/profiles/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to delete'); return; }
      fetchList();
    } catch (e) { console.error(e); }
  };

  const fmtRate = (p) => p.calculation_type?.includes('fixed') ? `$${p.fixed_amount || 0}` : p.rate ? `${p.rate}%` : '—';

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🗂️ Commission Profiles</h2>
        <button style={btnPrimary} onClick={() => setEditing('new')}>+ New Profile</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 220 }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Scope', 'Calc Type', 'Rate/Amount', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No profiles yet</td></tr>}
              {list.map(p => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{p.profile_code}</td>
                  <td style={tdStyle}>{p.name}</td>
                  <td style={tdStyle}>{(p.scope_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{(p.calculation_type || '').replace(/_/g, ' ')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtRate(p)}</td>
                  <td style={tdStyle}><Badge value={p.status} /></td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={btnSecondary} onClick={() => setEditing(p)}>Edit</button>
                      {p.status === 'draft' && <button style={btnSecondary} onClick={() => changeStatus(p.id, 'active')}>Activate</button>}
                      {p.status === 'active' && <button style={btnSecondary} onClick={() => changeStatus(p.id, 'inactive')}>Deactivate</button>}
                      {p.status === 'draft' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => removeDraft(p.id)}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ProfileModal companyId={companyId} profile={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
    </div>
  );
}

function ProfileModal({ companyId, profile, onClose, onSaved }) {
  const [form, setForm] = useState(profile ? { ...profile } : {
    name: '', description: '', scope_type: 'company', calculation_type: 'percentage',
    calculation_basis: 'after_discount', tax_treatment: 'after_tax', rate: '', fixed_amount: '',
    earning_trigger: 'customer_payment', min_commission: '', max_commission: '',
    cap_type: 'no_cap', cap_amount: '', holding_period_days: 0, applicable: true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const url = profile ? `${API}/companies/${companyId}/commissions/profiles/${profile.id}` : `${API}/companies/${companyId}/commissions/profiles`;
      const res = await fetch(url, {
        method: profile ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to save'); }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const u = (field, val) => setForm({ ...form, [field]: val });
  const isFixed = (form.calculation_type || '').includes('fixed') && !(form.calculation_type || '').includes('tiered');

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 620 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{profile ? 'Edit Profile' : 'New Commission Profile'}</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Name *"><input value={form.name || ''} onChange={e => u('name', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Scope Type">
            <select value={form.scope_type || 'company'} onChange={e => u('scope_type', e.target.value)} style={inputStyle}>
              {SCOPE_TYPES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Calculation Type">
            <select value={form.calculation_type || 'percentage'} onChange={e => u('calculation_type', e.target.value)} style={inputStyle}>
              {CALC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Calculation Basis">
            <select value={form.calculation_basis || 'after_discount'} onChange={e => u('calculation_basis', e.target.value)} style={inputStyle}>
              {CALC_BASIS.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Tax Treatment">
            <select value={form.tax_treatment || 'after_tax'} onChange={e => u('tax_treatment', e.target.value)} style={inputStyle}>
              <option value="before_tax">Before Tax</option><option value="after_tax">After Tax</option>
            </select>
          </FormRow>
          {isFixed ? (
            <FormRow label="Fixed Amount"><input type="number" value={form.fixed_amount || ''} onChange={e => u('fixed_amount', e.target.value)} style={inputStyle} /></FormRow>
          ) : (
            <FormRow label="Rate (%)"><input type="number" value={form.rate || ''} onChange={e => u('rate', e.target.value)} style={inputStyle} /></FormRow>
          )}
          <FormRow label="Earning Trigger">
            <select value={form.earning_trigger || 'customer_payment'} onChange={e => u('earning_trigger', e.target.value)} style={inputStyle}>
              {EARNING_TRIGGERS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Holding Period (days)"><input type="number" value={form.holding_period_days || 0} onChange={e => u('holding_period_days', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Min Commission"><input type="number" value={form.min_commission || ''} onChange={e => u('min_commission', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Max Commission"><input type="number" value={form.max_commission || ''} onChange={e => u('max_commission', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Cap Type">
            <select value={form.cap_type || 'no_cap'} onChange={e => u('cap_type', e.target.value)} style={inputStyle}>
              {CAP_TYPES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          {form.cap_type !== 'no_cap' && (
            <FormRow label="Cap Amount"><input type="number" value={form.cap_amount || ''} onChange={e => u('cap_amount', e.target.value)} style={inputStyle} /></FormRow>
          )}
          <FormRow label="Effective From"><input type="date" value={(form.effective_from || '').split('T')[0] || ''} onChange={e => u('effective_from', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Effective To"><input type="date" value={(form.effective_to || '').split('T')[0] || ''} onChange={e => u('effective_to', e.target.value)} style={inputStyle} /></FormRow>
        </div>
        <FormRow label="Description"><textarea value={form.description || ''} onChange={e => u('description', e.target.value)} style={{ ...inputStyle, minHeight: 50 }} /></FormRow>
        <FormRow label="Applicable">
          <input type="checkbox" checked={!!form.applicable} onChange={e => u('applicable', e.target.checked)} />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
